import {
  PlayButtonOverlay,
  PulseLoader,
  useMergedRefs,
} from '@sparemin/blockhead';
import cn from 'classnames';
import React, { useCallback, useRef, useState } from 'react';
import BemCSSTransition from 'components/BemCssTransition';
import ClipSelectPlayerPlaybar from './ClipSelectPlayerPlaybar';
import { useClipSelectVideoPlayerAnimation } from './ClipSelectVideoPlayerAnimationContext';
import { ANIMATION_DURATION_MS, CSS_VARS } from './constants';
import { basePlayerBlock as block } from './utils';

export type ClipSelectPlayerInstance = {
  pause: () => void;
  play: () => void;
  getCurrentTime: () => number;
  setCurrentTime: (sec: number) => void;
};

type ChildrenRenderProps<
  TRef extends ClipSelectPlayerInstance = ClipSelectPlayerInstance
> = {
  className: string;
  isPlaying: boolean;
  onPlay: () => void;
  onPause: () => void;
  onTimeUpdate: () => void;
  /**
   * The `videoRef` prop will be merged with this ref
   */
  ref: React.Ref<TRef>;
};

export interface ClipSelectPlayerContentsProps<
  TRef extends ClipSelectPlayerInstance = ClipSelectPlayerInstance
> {
  className?: string;
  children: (props: ChildrenRenderProps<TRef>) => React.ReactNode;
  duration: number;
  hoverProps: React.DOMAttributes<Element>;
  isLoading?: boolean;
  isPlaying: boolean;
  videoClassName?: string;
  videoRef?: React.Ref<TRef>;
  onPlaybackChange?: (playing: boolean) => void;
  onSeek?: (sec: number) => void;
}

function ClipSelectPlayerContents<
  TRef extends ClipSelectPlayerInstance = ClipSelectPlayerInstance
>({
  children,
  className,
  duration,
  hoverProps,
  isLoading,
  isPlaying,
  onPlaybackChange,
  onSeek,
  videoClassName,
  videoRef,
}: ClipSelectPlayerContentsProps<TRef>): React.ReactElement | null {
  const playerRef = useRef<TRef>();
  const mergedRef = useMergedRefs([videoRef, playerRef]);
  const [currentTime, setCurrentTime] = useState(0);
  const animations = useClipSelectVideoPlayerAnimation();

  const handlePlay = useCallback(() => {
    if (!isPlaying) {
      onPlaybackChange?.(true);
    }
  }, [isPlaying, onPlaybackChange]);

  const handlePause = useCallback(() => {
    if (isPlaying) {
      onPlaybackChange?.(false);
    }
  }, [isPlaying, onPlaybackChange]);

  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      handlePause();
    } else {
      handlePlay();
    }
  }, [handlePause, handlePlay, isPlaying]);

  const handleTimeUpdate = () => {
    setCurrentTime(playerRef.current?.getCurrentTime());
  };

  const handleSliderChange = useCallback(
    (value: number) => {
      playerRef.current?.setCurrentTime(value);
      setCurrentTime(value);
      onSeek?.(value);
    },
    [onSeek],
  );

  const videoElementClassName = cn(block('video'), videoClassName);

  const childrenRenderProps = {
    className: videoElementClassName,
    isPlaying,
    onPause: handlePause,
    onPlay: handlePlay,
    onTimeUpdate: handleTimeUpdate,
    ref: mergedRef,
  };

  return (
    <div
      className={cn(block({ loading: isLoading }), className)}
      {...animations.dataAttributes}
      style={
        {
          [CSS_VARS.ANIMATION_DURATION]: `${ANIMATION_DURATION_MS}ms`,
        } as React.CSSProperties
      }
      {...hoverProps}
    >
      {children?.(childrenRenderProps)}
      <BemCSSTransition className={block('overlay')} {...animations.overlay}>
        <div>
          <PlayButtonOverlay playing={isPlaying} onPress={handlePlayPause} />
        </div>
      </BemCSSTransition>
      <div className={block('progress-bar-with-duration-wrapper')}>
        <ClipSelectPlayerPlaybar
          duration={duration}
          currentTime={currentTime}
          onChange={handleSliderChange}
          maxValue={duration || 1}
        />
      </div>
      {isLoading && (
        <div className={block('loading-overlay')}>
          <PulseLoader />
        </div>
      )}
    </div>
  );
}

export default ClipSelectPlayerContents;
